import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components/macro';
import getInstructions from 'Education/api/instruction/getInstructions';
import Loader from 'Common/components/Loader';
import InstructionSidebar from 'Education/components/instruction/InstructionSidebar';
import InstructionsTable from 'Education/components/instruction/InstructionsTable';
import getInstruction from 'Education/api/instruction/getInstruction';
import axios from 'axios';
import useAxiosRequest from 'Common/hooks/useAxiosRequest';
import RetryLoading from 'Common/components/RetryLoading';
import notify from 'Common/utils/notify';
import { toast } from 'react-toastify';
import OuterBox from 'Common/components/Boxes/OuterBox';

const Wrapper = styled.div`
    @media screen and (min-width: 940px) {
        display: flex;
    }
`;

const Content = styled(OuterBox)`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    flex: 1;
    padding: 8px 0;
`;

const loadProperties = [
    'presentationAttachment',
    'slidesCount',
    'questionsCount',
    'areas',
];

const InstructionListTab = () => {
    const instructionsRequest = useCallback(
        cancelToken => getInstructions(loadProperties, cancelToken),
        [],
    );

    const {
        data: instructions,
        setData: setInstructions,
        loadData,
        isLoading,
        hasError,
    } = useAxiosRequest(instructionsRequest, []);

    const [selectedAreaIds, setSelectedAreaIds] = useState([]);

    const handleCheckboxChange = (id, isChecked) => {
        setSelectedAreaIds(prevSelectedAreaIds => {
            const newSelectedAreaIds = [...prevSelectedAreaIds];

            if (isChecked && !prevSelectedAreaIds.includes(id)) {
                newSelectedAreaIds.push(id);
            } else if (!isChecked && prevSelectedAreaIds.includes(id)) {
                const index = newSelectedAreaIds.indexOf(id);

                newSelectedAreaIds.splice(index, 1);
            }

            return newSelectedAreaIds;
        });
    };

    const source = useMemo(() => axios.CancelToken.source(), []);

    useEffect(
        () => () => {
            source.cancel();
        },
        [source],
    );

    const handleInstructionCreate = async newInstruction => {
        try {
            const response = await getInstruction(
                newInstruction.id,
                loadProperties,
                source.token,
            );

            setInstructions(prevInstructions => [
                ...prevInstructions,
                response.data,
            ]);
        } catch (error) {
            if (!axios.isCancel(error)) {
                notify(
                    'Ein Fehler ist aufgetreten. Bitte versuchen Sie, die Seite zu aktualisieren.',
                    {
                        type: toast.TYPE.ERROR,
                    },
                );
            }
        }
    };

    const handleInstructionArchive = id => {
        setInstructions(prevInstructions => {
            const newInstructions = [...prevInstructions];

            const index = newInstructions.findIndex(
                instruction => instruction.id === id,
            );

            if (index !== -1) {
                newInstructions.splice(index, 1);
            }

            return newInstructions;
        });
    };

    const [searchTerm, setSearchTerm] = useState('');

    let filteredInstructions = [...instructions];

    if (searchTerm) {
        filteredInstructions = filteredInstructions.filter(instruction =>
            instruction.name.toLowerCase().includes(searchTerm.toLowerCase()),
        );
    }

    if (selectedAreaIds.length > 0) {
        filteredInstructions = filteredInstructions.filter(instruction => {
            const instructionAreaIds = instruction.areas.map(area => area.id);

            const intersectingAreaIds = instructionAreaIds.filter(areaId =>
                selectedAreaIds.includes(areaId),
            );

            return intersectingAreaIds.length > 0;
        });
    }

    return (
        <>
            <Wrapper>
                <InstructionSidebar
                    searchTerm={searchTerm}
                    onSearchTermChange={setSearchTerm}
                    selectedAreaIds={selectedAreaIds}
                    onCheckboxChange={handleCheckboxChange}
                    onInstructionCreate={handleInstructionCreate}
                />
                <Content>
                    {isLoading ? (
                        <Loader />
                    ) : hasError ? (
                        <RetryLoading onRetry={loadData} />
                    ) : (
                        <InstructionsTable
                            instructions={filteredInstructions}
                            onInstructionArchive={handleInstructionArchive}
                        />
                    )}
                </Content>
            </Wrapper>
        </>
    );
};

export default InstructionListTab;
